import React from 'react';
import ProfileAssistantRow from '../../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../../GeneralProfile/ProfileUserAnswerRow';
import {
  shouldShowIneligibleInHistory,
  shouldShowStepInHistory,
  stepQuestionKey,
} from './spouseSectionSteps';
import type { SpouseSectionFlow } from './useSpouseSectionFlow';
import type { SpouseSectionStep } from './spouseSectionTypes';

function QuestionBlock({ flow, fieldStep }: { flow: SpouseSectionFlow; fieldStep: SpouseSectionStep }) {
  return <ProfileAssistantRow>{flow.t(stepQuestionKey(fieldStep))}</ProfileAssistantRow>;
}

function AnswerRow({
  flow,
  fieldStep,
  showUndo,
  children,
}: {
  flow: SpouseSectionFlow;
  fieldStep: SpouseSectionStep;
  showUndo: boolean;
  children: React.ReactNode;
}) {
  const showFieldEdit =
    flow.reviewMode &&
    !flow.editingFromReview &&
    fieldStep !== 'notMarriedDone' &&
    fieldStep !== 'ineligibleNotice';
  return (
    <ProfileUserAnswerRow
      showUndo={!showFieldEdit && showUndo}
      onUndo={() => void flow.handleUndo()}
      undoLabel={flow.undoLabel}
      showEdit={showFieldEdit}
      onEdit={() => void flow.beginEditField(fieldStep)}
      editLabel={flow.editLabel}
    >
      {children}
    </ProfileUserAnswerRow>
  );
}

export default function SpouseSectionChatThread({ flow }: { flow: SpouseSectionFlow }) {
  if (flow.maritalStatus === 'not_married' && flow.notMarriedCommitted) {
    return (
      <>
        <ProfileAssistantRow>{flow.t(stepQuestionKey('notMarriedDone'))}</ProfileAssistantRow>
        <AnswerRow flow={flow} fieldStep="notMarriedDone" showUndo={false}>
          {flow.userAnswerForStep('notMarriedDone')}
        </AnswerRow>
      </>
    );
  }

  return (
    <>
      {flow.addSpouseNameCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="addSpouseName" />
          <AnswerRow flow={flow} fieldStep="addSpouseName" showUndo={flow.showUndoAddSpouseName}>
            {flow.userAnswerForStep('addSpouseName')}
          </AnswerRow>
        </>
      )}
      {flow.addSpouseKanaCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="addSpouseKana" />
          <AnswerRow flow={flow} fieldStep="addSpouseKana" showUndo={flow.showUndoAddSpouseKana}>
            {flow.userAnswerForStep('addSpouseKana')}
          </AnswerRow>
        </>
      )}
      {flow.addSpouseDobCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="addSpouseDob" />
          <AnswerRow flow={flow} fieldStep="addSpouseDob" showUndo={flow.showUndoAddSpouseDob}>
            {flow.userAnswerForStep('addSpouseDob')}
          </AnswerRow>
        </>
      )}
      {flow.sharedLivelihoodCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="sharedLivelihood" />
          <AnswerRow flow={flow} fieldStep="sharedLivelihood" showUndo={flow.showUndoSharedLivelihood}>
            {flow.userAnswerForStep('sharedLivelihood')}
          </AnswerRow>
        </>
      )}
      {flow.employeeIncomeCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="employeeEstimatedIncome" />
          <AnswerRow flow={flow} fieldStep="employeeEstimatedIncome" showUndo={flow.showUndoEmployeeIncome}>
            {flow.userAnswerForStep('employeeEstimatedIncome')}
          </AnswerRow>
        </>
      )}
      {flow.familyWorkerCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="spouseFamilyWorker" />
          <AnswerRow flow={flow} fieldStep="spouseFamilyWorker" showUndo={flow.showUndoFamilyWorker}>
            {flow.userAnswerForStep('spouseFamilyWorker')}
          </AnswerRow>
        </>
      )}
      {flow.spouseIncomeCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="spouseEstimatedIncome" />
          <AnswerRow flow={flow} fieldStep="spouseEstimatedIncome" showUndo={flow.showUndoSpouseIncome}>
            {flow.userAnswerForStep('spouseEstimatedIncome')}
          </AnswerRow>
        </>
      )}
      {shouldShowIneligibleInHistory(flow.ineligibleCommitted, flow.decl) && (
        <>
          <ProfileAssistantRow>{flow.t(stepQuestionKey('ineligibleNotice'))}</ProfileAssistantRow>
          <AnswerRow flow={flow} fieldStep="ineligibleNotice" showUndo={false}>
            {flow.userAnswerForStep('ineligibleNotice')}
          </AnswerRow>
        </>
      )}
      {flow.confirmNameCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="confirmFullName" />
          <AnswerRow flow={flow} fieldStep="confirmFullName" showUndo={flow.showUndoConfirmName}>
            {flow.userAnswerForStep('confirmFullName')}
          </AnswerRow>
        </>
      )}
      {flow.confirmKanaCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="confirmFullNameKana" />
          <AnswerRow flow={flow} fieldStep="confirmFullNameKana" showUndo={flow.showUndoConfirmKana}>
            {flow.userAnswerForStep('confirmFullNameKana')}
          </AnswerRow>
        </>
      )}
      {flow.confirmMyNumberCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="confirmMyNumber" />
          <AnswerRow flow={flow} fieldStep="confirmMyNumber" showUndo={flow.showUndoConfirmMyNumber}>
            {flow.userAnswerForStep('confirmMyNumber')}
          </AnswerRow>
        </>
      )}
      {flow.confirmDobCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="confirmDob" />
          <AnswerRow flow={flow} fieldStep="confirmDob" showUndo={flow.showUndoConfirmDob}>
            {flow.userAnswerForStep('confirmDob')}
          </AnswerRow>
        </>
      )}
      {flow.sameHouseholdCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="sameHousehold" />
          <AnswerRow flow={flow} fieldStep="sameHousehold" showUndo={flow.showUndoSameHousehold}>
            {flow.userAnswerForStep('sameHousehold')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory('addressIfNot', flow.addressCommitted, flow.spouse, flow.decl) && (
        <>
          <QuestionBlock flow={flow} fieldStep="addressIfNot" />
          <AnswerRow flow={flow} fieldStep="addressIfNot" showUndo={flow.showUndoAddress}>
            {flow.userAnswerForStep('addressIfNot')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory('nonResident', flow.nonResidentCommitted, flow.spouse, flow.decl) && (
        <>
          <QuestionBlock flow={flow} fieldStep="nonResident" />
          <AnswerRow flow={flow} fieldStep="nonResident" showUndo={flow.showUndoNonResident}>
            {flow.userAnswerForStep('nonResident')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory(
        'nonResidentReason',
        flow.nonResidentReasonCommitted,
        flow.spouse,
        flow.decl
      ) && (
        <>
          <QuestionBlock flow={flow} fieldStep="nonResidentReason" />
          <AnswerRow flow={flow} fieldStep="nonResidentReason" showUndo={flow.showUndoNonResidentReason}>
            {flow.userAnswerForStep('nonResidentReason')}
          </AnswerRow>
        </>
      )}
      {flow.disabilityCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="disabilityStatus" />
          <AnswerRow flow={flow} fieldStep="disabilityStatus" showUndo={flow.showUndoDisability}>
            {flow.userAnswerForStep('disabilityStatus')}
          </AnswerRow>
        </>
      )}
      {flow.changeDuringYearCommitted && (
        <>
          <QuestionBlock flow={flow} fieldStep="changeDuringYear" />
          <AnswerRow flow={flow} fieldStep="changeDuringYear" showUndo={flow.showUndoChangeDuringYear}>
            {flow.userAnswerForStep('changeDuringYear')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory('changeDate', flow.changeDateCommitted, flow.spouse, flow.decl) && (
        <>
          <QuestionBlock flow={flow} fieldStep="changeDate" />
          <AnswerRow flow={flow} fieldStep="changeDate" showUndo={flow.showUndoChangeDate}>
            {flow.userAnswerForStep('changeDate')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory('changeReason', flow.changeReasonCommitted, flow.spouse, flow.decl) && (
        <>
          <QuestionBlock flow={flow} fieldStep="changeReason" />
          <AnswerRow flow={flow} fieldStep="changeReason" showUndo={flow.showUndoChangeReason}>
            {flow.userAnswerForStep('changeReason')}
          </AnswerRow>
        </>
      )}
      {shouldShowStepInHistory(
        'changeReasonOther',
        flow.changeReasonOtherCommitted,
        flow.spouse,
        flow.decl
      ) && (
        <>
          <QuestionBlock flow={flow} fieldStep="changeReasonOther" />
          <AnswerRow flow={flow} fieldStep="changeReasonOther" showUndo={false}>
            {flow.userAnswerForStep('changeReasonOther')}
          </AnswerRow>
        </>
      )}
      {!flow.isComplete &&
        !flow.reviewMode &&
        !flow.editingFromReview &&
        flow.step !== 'notMarriedDone' &&
        flow.step !== 'ineligibleNotice' && (
          <QuestionBlock flow={flow} fieldStep={flow.step} />
        )}
    </>
  );
}
